import { Group, GroupFilterCondition } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { chunk } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { LatestStatusData } from "@/models/LatestStatusData";
import { Preset } from "@/models/Preset";
import createGroupFilter from "@/utilities/createGroupFilter";

// GetLatestStatusDataByDevice not officially released by geotab API
export function useLatestStatusData(
  preset: Preset | null,
  deviceIds: string[],
  groups?: Group[],
  groupFilterCondition?: GroupFilterCondition,
  interval?: number,
  ignoreFromDate?: boolean,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    queryKey: [
      "latestStatusData",
      activeSessionId,
      preset?.id,
      deviceIds,
      groups,
      groupFilterCondition,
    ],

    queryFn: async ({ signal }): Promise<LatestStatusData | null> => {
      if (!preset || !preset.columns) {
        return Promise.resolve(null);
      }

      const chunkedDeviceIds = chunk(deviceIds, 1000); // Split deviceIds into chunks of 1000
      const filterPromises: Promise<LatestStatusData>[] = chunkedDeviceIds.map(chunk => {
        const filter = createGroupFilter(groups, groupFilterCondition, {
          preset,
          pageSize: 50000,
          page: 1,
          devices: chunk.map(dId => ({ id: dId })),
          ...(!ignoreFromDate && { fromDate: new Date(Date.now() - 2 * 60 * 60 * 1000) }),
        });
        return geotabClient.call("GetLatestStatusDataByDevice", filter, signal);
      });

      const results = await Promise.all(filterPromises);
      return {
        data: results.flatMap(result => result.data),
        totalPages: 1,
        totalRecords: 100000,
      };
    },

    refetchInterval: interval ?? 60000,
    placeholderData: keepPreviousData,
    enabled: !!preset && deviceIds?.length > 0,
  });
}
